// TagInput.tsx
import React, {
	useState,
	KeyboardEvent,
	ChangeEvent,
	useRef,
	useEffect,
} from "react";
import { P } from "common/P/P";
import { ReactComponent as CloseIcon } from "common/assets/icons/x.svg";
import { Input } from "common/Input/Input";

interface TagInputProps {
	placeholder?: string;
	label?: string;
	tags?: string[];
	onChange?: any;
}

const TagInput: React.FC<TagInputProps> = ({
	placeholder,
	label,
	tags: givenTags,
	onChange,
}) => {
	const [tags, setTags] = useState<string[]>([]);
	const [input, setInput] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (givenTags && givenTags.length > 0) {
			try {
				// Parse the first element of givenTags if it's a string
				const firstTag = givenTags[0];
				if (
					typeof firstTag === "string" &&
					firstTag.startsWith('["') &&
					firstTag.endsWith('"]')
				) {
					const parsedTags = JSON.parse(firstTag);
					setTags(parsedTags);
				} else {
					// If it's not a JSON string, use it as a regular array
					setTags(givenTags);
				}
			} catch (error) {
				console.error("Error parsing tags:", error);
			}
		}
	}, [givenTags]);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setInput(e.target.value);
	};

	const handleInputKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && input) {
			const newTags = [...tags, input];
			setTags(newTags);
			setInput("");
			if (onChange) {
				onChange(newTags);
			}
		}
	};

	const removeTag = (index: number) => {
		const newTags = tags.filter((_, i) => i !== index);
		setTags(newTags);
		if (onChange) {
			onChange(newTags);
		}
	};

	const focusInput = () => {
		inputRef.current?.focus();
	};

	return (
		<div className="grid grid-cols-1 gap-2">
			{label && (
				<label>
					<P size="t1" type="regular" className="text-textColor">
						{label}
					</P>
				</label>
			)}
			<div
				className="flex flex-wrap gap-2 px-[14px] py-2 border border-strokeColor rounded-md"
				onClick={focusInput}
			>
				{tags.map((tag, index) => (
					<div
						key={index}
						className="flex items-center gap-2 px-3 py-1 rounded bg-lowOpacity"
					>
						<P size="t1" type="medium" className="text-mainColor">
							{tag}
						</P>
						<CloseIcon
							onClick={() => removeTag(index)}
							className="w-4 h-4 cursor-pointer stroke-mainColor"
						/>
					</div>
				))}
				<input
					ref={inputRef}
					type="text"
					value={input}
					placeholder={tags.length === 0 ? placeholder : ""}
					onChange={handleInputChange}
					onKeyDown={handleInputKeyDown}
					className="flex-1 outline-none"
				/>
			</div>
		</div>
	);
};

export default TagInput;
